import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  diagnosticPageQuery,
  rebuildGeneralConfiguration,
  reportActionsQuery,
  reportSettingsQuery,
  saveReportActions,
  saveReportSettings,
  ticketStatusesQuery,
  type DiagnosticReportPage,
} from '../../api/settingsDiagnosticReports'
import { toast } from '../../ui/notify'
import { SelectPicker } from '../../ui/SelectPicker'
/* Capul ecranelor din zona de setari si bifa ei, cu foaia lor. Aceleasi
   piese ca pe Customers, Inventoree si Softsee. */
import { SettingsCheck, SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Diagnostic reports: computer_scan / network_scan / security_scan /
 * skus_report / general_settings, element cu element fata de
 * version-3/settings/diagnostic_reports/.
 */

export function DiagnosticReportsSettingsPage({ page }: { page: DiagnosticReportPage }) {
  if (page === 'general_settings') return <GeneralSettingsPanel />
  return <ScanPanel page={page} />
}

function Forbidden() {
  return (
    <div className="alert alert-danger">
      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
    </div>
  )
}

function ScanPanel({ page }: { page: DiagnosticReportPage }) {
  const shell = useQuery(diagnosticPageQuery(page))
  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />
  return (
    <>
      {/* Capul zonei. Randul gri purta `marginBottom: isSkus ? 0 : 0` - o
          alegere intre doua lucruri identice, adica o intrebare care nu se pune. */}
      <SettingsHead
        strong={shell.data.title}
        rest="settings"
        subtitle={
          <>
            Manage <strong>report settings and actions</strong>
          </>
        }
      />
      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ width: 300 }}>Report settings</h5>
          </div>
          <div className="col-md-10">
            <div className="report-settings">
              <ReportSettingsForm companyId={shell.data.company_id} reportId={shell.data.report_id} />
            </div>
          </div>
        </div>
        <hr className="dotted" />
      </section>
      {page !== 'skus_report' ? (
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ width: 300 }}>Actions settings</h5>
            </div>
            <div className="col-md-10">
              <div className="report-actions">
                <ReportActionsForm companyId={shell.data.company_id} reportId={shell.data.report_id} />
              </div>
            </div>
          </div>
          <hr className="dotted" />
        </section>
      ) : null}
    </>
  )
}

function ReportSettingsForm({ companyId, reportId }: { companyId: number; reportId: number }) {
  const query = useQueryClient()
  const page = useQuery(reportSettingsQuery(companyId, reportId))
  const [preview, setPreview] = useState('')
  /* Ce s-a scris in campul culorii de cand s-a deschis ecranul; `null` =
     nimic inca, deci pata poarta culoarea salvata. */
  const [colorDraft, setColorDraft] = useState<string | null>(null)
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />
  const settings = page.data.settings
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const body = new FormData(event.currentTarget)
    void saveReportSettings(body)
      .then(() => {
        toast('Settings changed', 'Success', 'success')
        void query.invalidateQueries({ queryKey: ['settings', 'diagnostic_reports', 'settings'] })
      })
      .catch(() => toast('Something went wrong.', 'Error', 'error'))
  }
  return (
    <form className="report_settings" action="/settings/diagnostic_reports/saveReportSettings" method="post" onSubmit={onSubmit}>
      <input type="hidden" name="company_id" value={companyId} />
      <input type="hidden" name="report_id" value={reportId} />
      {/*
       * Randurile erau cartonase albe cu bordura de 6px in laturi, unul sub
       * altul INAUNTRUL cartonasului sectiunii - un carton in alt carton - iar
       * numele campului statea intr-un `h5` plutit la stanga, urmat de o
       * coloana goala: campul incepea abia la 481px de marginea randului.
       *
       * Acum sunt campurile zonei: eticheta DEASUPRA campului, trei pe un rand,
       * fiecare la acelasi x pe toate randurile.
       */}
      <div className="row">
        <div className="col-md-4 form-group">
          <label htmlFor="reportTitle">Report title</label>
          <input id="reportTitle" type="text" className="form-control" name="settings[report_title]" defaultValue={settings.report_title} />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="reportColor">Report color</label>
          {/*
           * Pata de culoare intra in campul ei, ca addon. Inainte era o coloana
           * separata, si se schimba prin `closest('.row')` plus
           * `querySelector` - adica prin scotocirea paginii dupa un vecin.
           */}
          <div className="input-group">
            <span className="input-group-addon">
              <span className="badge" style={{ minWidth: 23, minHeight: 23, backgroundColor: colorDraft ?? settings.report_color }}>
                {' '}
              </span>
            </span>
            <input
              id="reportColor"
              type="text"
              className="form-control colorpicker"
              name="settings[report_color]"
              defaultValue={settings.report_color}
              onChange={(ev) => setColorDraft(ev.target.value)}
            />
          </div>
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="companyPhone">Company phone no.</label>
          <input id="companyPhone" type="text" className="form-control" name="settings[company_phone]" defaultValue={settings.company_phone} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-4 form-group">
          <label htmlFor="reportLogo">Report logo</label>
          <div>
            <img id="uploaded-logo" style={{ width: 50, verticalAlign: 'middle', marginRight: 8 }} src={preview || page.data.logo_src} alt="" />
            {/* Un singur buton primar pe formular, si ala e "Update"; incarcarea
                siglei e o unealta langa camp, deci e buton alb. */}
            <button
              type="button"
              id="reportLogo"
              data-loading-text="Uploading.."
              className="btn btn-default btn-sm upload-report-logo-button"
              onClick={(ev) => {
                ev.preventDefault()
                ev.currentTarget.closest('div')?.querySelector<HTMLInputElement>('.upload-report-logo')?.click()
              }}
            >
              <i className="fa fa-paperclip" aria-hidden="true"></i> Upload logo
            </button>
            <input
              type="file"
              className="form-control hidden upload-report-logo"
              name="report_logo"
              accept=".jpg, .jpeg, .png"
              onChange={(ev) => {
                const file = ev.currentTarget.files?.[0]
                if (!file) return
                const reader = new FileReader()
                reader.onload = () => setPreview(String(reader.result ?? ''))
                reader.readAsDataURL(file)
              }}
            />
          </div>
        </div>
      </div>
      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-md-12">
          <button type="submit" className="btn btn-primary pull-right update-report-settings">
            Update
          </button>
        </div>
      </div>
    </form>
  )
}

function ReportActionsForm({ companyId, reportId }: { companyId: number; reportId: number }) {
  const query = useQueryClient()
  const page = useQuery(reportActionsQuery(companyId, reportId))
  const [ticketType, setTicketType] = useState<number | null>(null)
  /* Starea aleasa, ca si tipul: `null` = n-a atins-o nimeni, deci tine ce
     era salvat. Se sterge cand se schimba tipul - o stare a altui tip de
     tichet nu mai inseamna nimic. */
  const [ticketStatus, setTicketStatus] = useState<number | null>(null)
  const selectedType = ticketType ?? page.data?.selected_ticket_type ?? 0
  const statuses = useQuery(ticketStatusesQuery(selectedType))
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />
  const checked = Number(page.data.actions?.change_ticket_status?.checked ?? 0) === 1
  const statusMap = statuses.data ?? page.data.ticket_statuses
  const selectedStatus = ticketStatus ?? page.data.selected_ticket_status ?? 0
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const body = new FormData(event.currentTarget)
    void saveReportActions(body)
      .then(() => {
        toast('Actions changed', 'Success', 'success')
        void query.invalidateQueries({ queryKey: ['settings', 'diagnostic_reports', 'actions'] })
      })
      .catch(() => toast('Something went wrong.', 'Error', 'error'))
  }
  return (
    <form className="report_actions" action="/settings/diagnostic_reports/saveReportActions" method="post" onSubmit={onSubmit}>
      <input type="hidden" name="company_id" value={companyId} />
      <input type="hidden" name="report_id" value={reportId} />
      {/*
       * Aici era defectul, nu doar desenul: cele doua liste erau
       * `<select className="form-control selectpicker">`, iar `selectpicker`
       * e CARLIGUL lui bootstrap-select, care in v4 nu se incarca. `app.css`
       * ascunde selectul ca pluginul sa-i puna lista in loc; fara plugin nu-l
       * inlocuieste nimeni. Masurat in Edge cu foile de productie: amandoua
       * ieseau `display: none`, 0x0 - adica pe Computer, Network si Security
       * Scan nu se putea alege NICI tipul, NICI starea tichetului.
       *
       * Acum sunt `SelectPicker`, lista zonei, cu `form-control` pe invelis.
       * Selectul adevarat ramane in pagina, ascuns, cu acelasi `name`, deci
       * POST-ul pleaca neschimbat.
       *
       * Si bifa: era `<input className="js-switch">`, carligul lui Switchery -
       * alt plugin care nu se incarca, si care n-are nicio regula in foile
       * servite. Iesea o casuta goala de 15px, plutita la dreapta, fara nicio
       * eticheta langa ea. Acum e bifa zonei, lipita de textul care o explica.
       */}
      <div className="row">
        <div className="col-md-4 form-group">
          <label htmlFor="changeTicketStatus">
            Change ticket status{' '}
            <i
              className="fas fa-info-circle color-blue font-14"
              style={{ cursor: 'default' }}
              data-toggle="tooltip"
              data-placement="top"
              title="Change ticket status when report is generated"
            >
              {' '}
            </i>
          </label>
          <SettingsCheck
            id="changeTicketStatus"
            name="settings[change_ticket_status][checked]"
            label="When the report is generated"
            defaultChecked={checked}
          />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="ticketType">Ticket type</label>
          <SelectPicker
            id="ticketType"
            className="form-control"
            name="settings[change_ticket_status][values][ticket_type]"
            title="Select ticket type.."
            value={selectedType > 0 ? String(selectedType) : ''}
            onChange={(value) => {
              setTicketType(Number(value) || 0)
              setTicketStatus(0)
            }}
            options={page.data.ticket_types.map((type) => ({ value: String(type.ID), label: type.name }))}
          />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="ticketStatus">Ticket status</label>
          <SelectPicker
            id="ticketStatus"
            className="form-control"
            name="settings[change_ticket_status][values][ticket_status]"
            title="Select ticket status.."
            value={selectedStatus > 0 ? String(selectedStatus) : ''}
            onChange={(value) => setTicketStatus(Number(value) || 0)}
            options={Object.entries(statusMap).map(([id, name]) => ({ value: id, label: name }))}
          />
        </div>
      </div>
      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-md-12">
          <button type="submit" className="btn btn-primary pull-right update-report-actions">
            Update
          </button>
        </div>
      </div>
    </form>
  )
}

function GeneralSettingsPanel() {
  const shell = useQuery(diagnosticPageQuery('general_settings'))
  const [busy, setBusy] = useState(false)
  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setBusy(true)
    void rebuildGeneralConfiguration()
      .then(() => toast('Configuration build successfully.', 'Success', 'success'))
      .catch(() => toast('Something went wrong.', 'Error', 'error'))
      .finally(() => setBusy(false))
  }
  return (
    <>
      {/*
       * Capul zonei. Titlul era singur - `<h4><strong>Settings</strong></h4>` -
       * fara randul gri de sub el, singurul ecran din meniu fara el (masurat:
       * `label.color` lipsea cu totul). Si "Settings" singur nu spune pe ce
       * ecran stai: in meniu randul se cheama "General Settings".
       */}
      <SettingsHead
        strong="General"
        rest="settings"
        subtitle={
          <>
            Rebuild the <strong>reports configuration</strong> after a change
          </>
        }
      />
      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ marginBottom: 20 }}>Reports configuration</h5>
          </div>
          <div className="col-md-10">
            <form action="/settings/diagnostic_reports/rebuild_general_configuration" method="post" id="rebuildConfigurationForm" onSubmit={onSubmit}>
              {/* `form-group row` > `col-md-12` era o coloana care tine toata
                  latimea, adica un rand in plus fara nimic in el. */}
              <div className="form-group">
                <button type="submit" className="btn btn-sm btn-success" disabled={busy}>
                  <i className="fas fa-cogs" aria-hidden="true"></i> Rebuild configuration
                </button>
                <span className="help-block">
                  Rebuild configuration after every change to speed up the application and load new changes.
                  <br />
                  New settings will take effect after you do the <strong>REBUILD</strong>.
                </span>
              </div>
            </form>
          </div>
        </div>
        <hr className="dotted" />
      </section>
    </>
  )
}
